import React, { useEffect, useRef, useCallback } from "react";
import { FaChevronLeft } from "react-icons/fa";
import "./Modal.css";

const Modal = React.forwardRef(
  (
    {
      show,
      onHide,
      title,
      body,
      footer,
      modalName,
      backToPrevious = "",
      handleBackBtn,
      type = "modal-md",
    },
    ref
  ) => {
    const modalRef = useRef(null);
    const modalInstanceRef = useRef(null);
    const isInitializedRef = useRef(false);
    const combinedRef = ref || modalRef;

    const cleanup = useCallback(() => {
      try {
        const modalElement = combinedRef.current;
        if (modalElement && modalInstanceRef.current) {
          modalElement.removeEventListener("hidden.bs.modal", onHide);
          modalElement.removeEventListener("shown.bs.modal", () => {});

          if (typeof modalInstanceRef.current.dispose === "function") {
            modalInstanceRef.current.dispose();
          }

          modalInstanceRef.current = null;
        }

        document.body.style.paddingRight = "";
        document.body.classList.remove("modal-open");

        const backdrops = document.querySelectorAll(".modal-backdrop");
        backdrops.forEach((backdrop) => backdrop.remove());

        isInitializedRef.current = false;
      } catch (error) {
        console.warn("Modal cleanup error handled:", error.message);
      }
    }, [combinedRef, onHide]);

    const showModal = useCallback(() => {
      try {
        const modalElement = combinedRef.current;

        if (!modalElement) {
          console.warn("Modal element not found");
          return;
        }

        if (!window.bootstrap || !window.bootstrap.Modal) {
          console.warn("Bootstrap Modal not available");
          return;
        }

        if (modalInstanceRef.current) {
          cleanup();
        }

        setTimeout(() => {
          try {
            // Double-check that the element still exists
            if (!modalElement || !document.contains(modalElement)) {
              console.warn("Modal element no longer exists");
              return;
            }

            modalInstanceRef.current = new window.bootstrap.Modal(
              modalElement,
              {
                backdrop: "static",
                keyboard: false,
                focus: false,
              }
            );

            // Extra hardening against Bootstrap focus errors
            try {
              // Ensure Bootstrap has a non-null triggering element to return focus to
              if (!modalInstanceRef.current._triggeringElement) {
                modalInstanceRef.current._triggeringElement = document.activeElement || document.body;
              }
              // Double-disable auto focus just in case
              if (modalInstanceRef.current._config) {
                modalInstanceRef.current._config.focus = false;
                modalInstanceRef.current._config.keyboard = false;
              }
              // Provide a safe, no-op focus trap to avoid calling focus() on null
              if (!modalInstanceRef.current._focustrap) {
                modalInstanceRef.current._focustrap = {
                  activate: () => {},
                  deactivate: () => {},
                  pause: () => {},
                  unpause: () => {},
                };
              }
            } catch (_) {}

            const handleHidden = () => {
              if (typeof onHide === "function") {
                onHide();
              }
              cleanup();
            };

            const handleShown = () => {
              document.body.style.paddingRight = "0px";
              // Prevent Bootstrap from attempting to autofocus inside modal
              try {
                if (modalInstanceRef.current) {
                  modalInstanceRef.current._isShown = true;
                  // Set a safe focus target
                  const safeTarget = modalElement.querySelector("[autofocus]") || modalElement;
                  if (safeTarget && typeof safeTarget.focus === "function") {
                    // Do not actually focus to avoid errors; just ensure method exists
                  }
                }
              } catch (_) {}
            };

            modalElement.addEventListener("hidden.bs.modal", handleHidden, {
              once: true,
            });
            modalElement.addEventListener("shown.bs.modal", handleShown, {
              once: true,
            });

            // Show modal with error handling
            try {
              modalInstanceRef.current.show();
              isInitializedRef.current = true;
            } catch (showError) {
              console.warn("Modal show failed:", showError.message);
              // Fallback to manual display
              if (modalElement && document.contains(modalElement)) {
                modalElement.style.display = "block";
                modalElement.classList.add("show", "fade");
                modalElement.setAttribute("aria-modal", "true");
                modalElement.setAttribute("role", "dialog");
                document.body.classList.add("modal-open");
              }
            }
          } catch (error) {
            console.warn("Modal show error:", error.message);
            // Fallback modal display
            if (modalElement && document.contains(modalElement)) {
              modalElement.style.display = "block";
              modalElement.classList.add("show", "fade");
              modalElement.setAttribute("aria-modal", "true");
              modalElement.setAttribute("role", "dialog");
              document.body.classList.add("modal-open");

              const backdrop = document.createElement("div");
              backdrop.className = "modal-backdrop fade show";
              document.body.appendChild(backdrop);
            }
          }
        }, 50);
      } catch (error) {
        console.warn("Show modal error handled:", error.message);
      }
    }, [combinedRef, cleanup, onHide]);

    const hideModal = useCallback(() => {
      try {
        const modalElement = combinedRef.current;

        if (!modalElement) return;

        if (
          modalInstanceRef.current &&
          typeof modalInstanceRef.current.hide === "function"
        ) {
          // Ensure _triggeringElement is valid before hiding to prevent focus errors
          if (modalInstanceRef.current._triggeringElement) {
            // Check if the triggering element still exists in the DOM
            if (
              !document.contains(modalInstanceRef.current._triggeringElement)
            ) {
              // Element was removed, set to a safe fallback
              modalInstanceRef.current._triggeringElement = document.body;
            }
          } else {
            // No triggering element set, use safe fallback
            modalInstanceRef.current._triggeringElement = document.body;
          }

          // Override focus behavior to prevent errors
          const originalFocus = modalInstanceRef.current._triggeringElement?.focus;
          if (modalInstanceRef.current._triggeringElement) {
            modalInstanceRef.current._triggeringElement.focus = function () {
              try {
                if (this && document.contains(this)) {
                  if (originalFocus && typeof originalFocus === "function") {
                    originalFocus.call(this);
                  }
                }
              } catch (e) {
                // Silently ignore focus errors
              }
            };
          }

          modalInstanceRef.current.hide();
        } else {
          modalElement.style.display = "none";
          modalElement.classList.remove("show");
          modalElement.removeAttribute("aria-modal");
          modalElement.removeAttribute("role");
          document.body.classList.remove("modal-open");

          const backdrops = document.querySelectorAll(".modal-backdrop");
          backdrops.forEach((backdrop) => backdrop.remove());

          if (typeof onHide === "function") {
            setTimeout(() => onHide(), 100);
          }
        }
      } catch (error) {
        console.warn("Hide modal error handled:", error.message);
        // Fallback cleanup if hide() fails
        try {
          const modalElement = combinedRef.current;
          if (modalElement) {
            modalElement.style.display = "none";
            modalElement.classList.remove("show");
            document.body.classList.remove("modal-open");
            const backdrops = document.querySelectorAll(".modal-backdrop");
            backdrops.forEach((backdrop) => backdrop.remove());
          }
          if (typeof onHide === "function") {
            setTimeout(() => onHide(), 100);
          }
        } catch (fallbackError) {
          console.warn("Fallback cleanup error:", fallbackError.message);
        }
      }
    }, [combinedRef, onHide]);

    useEffect(() => {
      if (show && !isInitializedRef.current) {
        showModal();
      } else if (!show && isInitializedRef.current) {
        hideModal();
      }

      return () => {
        if (isInitializedRef.current) {
          cleanup();
        }
      };
    }, [show, showModal, hideModal, cleanup]);

    useEffect(() => {
      return () => {
        cleanup();
      };
    }, [cleanup]);

    if (!show) return null;

    return (
      <div
        className={`modal fade custom-modal`}
        id={modalName}
        tabIndex="-1"
        aria-labelledby={`${modalName}Label`}
        aria-hidden="true"
        ref={combinedRef}
        data-bs-focus="false"
        style={{ display: "none" }}
      >
        <div
          className={`modal-dialog ${type} modal-dialog-centered modal-dialog-scrollable`}
        >
          <div className="modal-content">
            <div className="modal-header">
              {backToPrevious !== "" ? (
                <button
                  type="button"
                  className="btn back-btn btn-info"
                  onClick={(e) => {
                    e.preventDefault();
                    if (typeof handleBackBtn === "function") {
                      handleBackBtn();
                    }
                  }}
                >
                  <FaChevronLeft />
                  {backToPrevious}
                </button>
              ) : null}
              <h1 className="modal-title" id={`${modalName}Label`}>
                {title}
              </h1>
              <button
                type="button"
                className="btn-close"
                onClick={(e) => {
                  e.preventDefault();
                  hideModal();
                }}
                aria-label="Close"
              ></button>
            </div>
            {body && <div className="modal-body">{body}</div>}
            {footer && <div className="modal-footer">{footer}</div>}
          </div>
        </div>
      </div>
    );
  }
);

Modal.displayName = "Modal";

export default Modal;
